import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { X } from "lucide-react";
import { COR_SEV, rotuloEvento, severidade } from "../lib/eventos";
import type { Evento } from "../lib/traccar";

/**
 * Avisos ao vivo, canto inferior direito.
 *
 * Aparecem quando o servidor empurra um evento pelo WebSocket — e isso só
 * acontece para notificação cadastrada com o canal **web**
 * (`NotificatorWeb.sendAsync` → `connectionManager.updateEvent`). Ou seja: o
 * filtro é do servidor, não daqui. Chegou pelo socket, é porque alguém pediu
 * para ver na tela.
 *
 * ⚠️ O texto vem pronto em `attributes.message`: é o `digest` do modelo de
 * notificação, já no idioma do servidor. Remontar a frase aqui daria duas
 * versões da mesma mensagem, e a do e-mail é a que o cliente conhece.
 *
 * ⚠️ A entrada anima POSIÇÃO, nunca opacidade. Quatro vezes nesta base um
 * elemento com `initial={{opacity:0}}` ficou invisível porque o quadro de
 * animação não veio (aba em segundo plano, `prefers-reduced-motion`); aviso
 * que não aparece é pior que aviso sem animação.
 */
const PERMANENCIA_MS = 3000;
/** Acima disso a pilha vira parede: os mais antigos saem na frente. */
const TETO = 5;

export type Aviso = { chave: string; evento: Evento; nome: string };

export function Avisos({ avisos, aoFechar }: {
  avisos: Aviso[];
  aoFechar: (chave: string) => void;
}) {
  return (
    <div className="pointer-events-none fixed bottom-4 right-4 z-[2000] flex w-[min(92vw,360px)]
                    flex-col items-end gap-2"
         role="status" aria-live="polite">
      <AnimatePresence initial={false}>
        {avisos.slice(-TETO).map(a => (
          <Cartao key={a.chave} aviso={a} aoFechar={() => aoFechar(a.chave)} />
        ))}
      </AnimatePresence>
    </div>
  );
}

function Cartao({ aviso, aoFechar }: { aviso: Aviso; aoFechar: () => void }) {
  const [pausado, setPausado] = useState(false);

  // O relógio para enquanto o mouse está em cima: ler um alarme e ele sumir
  // no meio da frase é o jeito mais rápido de o operador desconfiar da tela.
  useEffect(() => {
    if (pausado) return;
    const t = setTimeout(aoFechar, PERMANENCIA_MS);
    return () => clearTimeout(t);
  }, [pausado, aoFechar]);

  const sev = severidade(aviso.evento.type);
  const alarme = typeof aviso.evento.attributes?.alarm === "string"
    ? aviso.evento.attributes.alarm : undefined;
  // O título é o EVENTO e o corpo é a frase do servidor, que já começa pelo
  // nome do veículo. Pôr o nome nos dois lugares fazia "Frota 02 / Frota 02
  // saiu da geocerca…" — a mesma informação duas vezes, na mesma caixa.
  const titulo = rotuloEvento(aviso.evento.type, alarme);
  const mensagem = typeof aviso.evento.attributes?.message === "string"
    ? aviso.evento.attributes.message
    : aviso.nome;

  return (
    <motion.div
      layout
      initial={{ x: 32 }}
      animate={{ x: 0 }}
      exit={{ x: 32, opacity: 0 }}
      transition={{ duration: 0.18, ease: [0.4, 0, 0.2, 1] }}
      onMouseEnter={() => setPausado(true)}
      onMouseLeave={() => setPausado(false)}
      className="pointer-events-auto flex w-full items-start gap-2.5 rounded-[var(--radius-card)]
                 border border-ink-200 bg-white p-3 shadow-[var(--shadow-lg)]
                 dark:border-ink-700 dark:bg-ink-900">
      {/* A cor não carrega a informação sozinha: o nome do evento vem escrito. */}
      <span className="mt-1 h-2 w-2 shrink-0 rounded-full" aria-hidden
            style={{ background: COR_SEV[sev] }} />
      <div className="min-w-0 flex-1">
        <p className="truncate text-[13px] font-semibold">{titulo}</p>
        <p className="mt-0.5 text-xs text-ink-600 dark:text-ink-300">{mensagem}</p>
      </div>
      <button onClick={aoFechar} aria-label="Fechar aviso"
        className="shrink-0 rounded p-0.5 text-ink-400 t-fast hover:bg-ink-100 dark:hover:bg-ink-800">
        <X size={13} />
      </button>
    </motion.div>
  );
}
